import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { sanitizeEmailHtml } from '../utils/sanitizeHtml';

// Renderizador do corpo do email em iframe isolado.
// Segurança: o sandbox NÃO inclui allow-scripts — scripts jamais executam,
// mesmo que algo escape da sanitização. allow-same-origin permanece apenas
// para permitir o auto-ajuste de altura via contentDocument.
export default function EmailBodyFrame({ html, theme }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(300);
  const cleanupRef = useRef([]);

  const srcdoc = useMemo(() => {
    if (!html) return '';

    const bgColors = { light: '#FFFFFF', dark: '#1A1D2E', midnight: '#000000' };
    const textColors = { light: '#111827', dark: '#F1F5F9', midnight: '#E2E8F0' };
    const linkColors = { light: '#2563EB', dark: '#60A5FA', midnight: '#60A5FA' };
    const bg = bgColors[theme] || bgColors.light;
    const text = textColors[theme] || textColors.light;
    const link = linkColors[theme] || linkColors.light;

    const sanitized = sanitizeEmailHtml(html);

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>
      *, *::before, *::after { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: ${bg}; color: ${text};
        font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px; line-height: 1.7; word-wrap: break-word; overflow-wrap: break-word; }
      a { color: ${link}; }
      img { max-width: 100%; height: auto; }
      table { max-width: 100% !important; }
      pre, code { white-space: pre-wrap; word-break: break-all; max-width: 100%; overflow-x: auto; }
      blockquote { margin: 8px 0; padding-left: 12px; border-left: 3px solid ${theme === 'light' ? '#E5E7EB' : '#334155'}; color: ${theme === 'light' ? '#6B7280' : '#94A3B8'}; }
    </style></head><body>${sanitized}</body></html>`;
  }, [html, theme]);

  // Cancela timers/listeners pendentes ao desmontar ou trocar de email
  useEffect(() => {
    return () => {
      cleanupRef.current.forEach(fn => fn());
      cleanupRef.current = [];
    };
  }, [srcdoc]);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      // Garantia extra além do <base target="_blank">
      doc.querySelectorAll('a[href]').forEach(a => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });

      let alive = true;
      const resize = () => {
        if (!alive) return;
        try {
          const h = doc.documentElement?.scrollHeight || doc.body?.scrollHeight || 300;
          setHeight(Math.max(100, Math.min(h + 16, 5000)));
        } catch (e) {}
      };

      resize();
      const t1 = setTimeout(resize, 200);
      const t2 = setTimeout(resize, 800);

      const images = Array.from(doc.querySelectorAll('img'));
      images.forEach(img => {
        if (!img.complete) img.addEventListener('load', resize);
      });

      cleanupRef.current.push(() => {
        alive = false;
        clearTimeout(t1);
        clearTimeout(t2);
        images.forEach(img => img.removeEventListener('load', resize));
      });
    } catch (e) {}
  }, []);

  if (!html) {
    return <div className="body-content" style={{ color: 'var(--text-tertiary)' }}>(Sem conteúdo)</div>;
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcdoc}
      onLoad={handleLoad}
      title="Conteúdo do email"
      style={{
        width: '100%',
        height: height,
        border: 'none',
        borderRadius: 8,
        maxWidth: 720,
        background: 'transparent',
      }}
    />
  );
}
